export const projects = [
  {
    slug: "valiant-un-bid",
    route: "corporate",
    title: "UN Financial Bid Proposal",
    client: "Valiant Integrated Services",
    year: "Undisclosed",
    role: "Lead Document Designer & Project Manager",
    sector: "International Aid & Defence Services",
    tags: ["Bid Design", "Information Design", "Governance", "Project Management"],
    summary:
      "Complete design and project management of a UN-facing financial bid proposal: hundreds of pages of highly sensitive financial and operational information, delivered submission-ready for an aid mission in Mali.",
    heroImage: "/images/valiant-un-bid-submission.jpeg",
  },
  {
    slug: "cathay-pacific-sustainability",
    route: "corporate",
    title: "Sustainability in Flight",
    client: "Cathay Pacific",
    year: "2024/25",
    role: "Creative Lead, Hong Kong agency",
    sector: "Aviation",
    tags: ["Campaign", "Illustration", "Sustainability"],
    summary:
      "A high-budget sustainability campaign to eliminate single-use plastics onboard and at corporate HQ, turning an operational shift into a movement people could feel.",
    heroImage: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/48af9aef-47b0-4cc9-a7fc-6cbc0ddb45ee/CathayGraphic+mock.jpg",
    images: [
      "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/c2b248b3-eb0f-461f-9820-805d0b01c9b8/CathayGraphic.jpg",
    ],
    metrics: [
      { value: "B2B+B2C", label: "Audiences balanced" },
      { value: "3", label: "Environments: airports, HQ, inflight" },
      { value: "360°", label: "Print to programmatic" },
      { value: "HK", label: "Led from Hong Kong" },
    ],
    chapters: [
      {
        num: "01",
        title: "Context & Mandate",
        body: [
          "As creative lead for a Hong Kong-based agency, I spearheaded Cathay Pacific's sustainability campaign: a commitment to eliminate single-use plastics onboard flights and at their corporate headquarters.",
          "The challenge was twofold: speak to business partners and passengers at once, and make an operational change feel like a shared ambition rather than a corporate memo.",
        ],
      },
      {
        num: "02",
        title: "Strategy & Governance",
        body: [
          "The strategy: emotive storytelling over policy-speak. Bold, human-centric visuals reframed the plastics pledge as a climate-action movement, giving every audience, B2B and B2C, a way in.",
          "Working within Cathay's brand governance, every asset was built to hold up in the most scrutinised environments the brand owns: airports, corporate spaces and the cabin itself.",
        ],
      },
      {
        num: "03",
        title: "Execution & Craft",
        body: [
          "The visual identity ran across illustrated posters, printed advertising and digital assets, displayed in airports, corporate spaces and inflight.",
          "The campaign extended into targeted social media ads, programmatic banners and immersive web content, keeping one visual voice from a six-sheet poster to a mobile banner.",
        ],
      },
      {
        num: "04",
        title: "Trade-offs & Decisions",
        body: [
          "The key tension: bold enough to move passengers, disciplined enough for boardrooms. The answer was illustration-led storytelling with a corporate-strength grid: warmth carried by the image, credibility held by the structure.",
        ],
      },
      {
        num: "05",
        title: "Outcome",
        body: [
          "The campaign drove awareness of Cathay Pacific's climate commitments across every audience it touched, proof that strategic storytelling and high-impact creative can turn an internal operations decision into public brand equity.",
        ],
      },
    ],
  },
  {
    slug: "jp-morgan-chase",
    route: "corporate",
    title: "Brand Communications",
    client: "JP Morgan & Chase",
    year: "2023/24",
    role: "Design & Art Direction",
    sector: "Financial Services",
    tags: ["Brand", "Campaign", "Communications"],
    summary:
      "Design and art direction for one of the world's largest financial institutions. Brand communications produced inside a global system where precision is the baseline, not the ambition.",
    heroImage: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1717585003277-GU6TWZN5BPEXT0BUROSI/Screenshot+2024-06-05+at+11.55.52.png",
    images: [
      "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1672920384138-KL0NUJIRN5SNTMA12UJ9/Screenshot+2023-01-04+at+16.27.46.png",
      "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1672920389216-0M5VKNCZGB2K8EG35B8V/Screenshot+2023-01-04+at+16.28.08.png",
    ],
    metrics: [
      { value: "Global", label: "Brand system" },
      { value: "B2B+B2C", label: "Audiences served" },
      { value: "Multi-team", label: "Stakeholder sign-off" },
      { value: "2023/24", label: "Engagement period" },
    ],
    chapters: [
      {
        num: "01",
        title: "Context & Mandate",
        body: [
          "JP Morgan & Chase runs one of the most closely governed brand systems in financial services. Every piece of communication, internal or client-facing, print or digital, has to clear brand, legal and compliance review before it goes anywhere near an audience.",
          "I was brought in to design and art-direct brand communications inside that system. The brief, in practice: meet the bar every time, and still produce work people choose to read.",
        ],
      },
      {
        num: "02",
        title: "Strategy & Governance",
        body: [
          "I treat a brand guideline as a material, not a rulebook. Typography, grid, colour and tone were the fixed points; my job was to find the range inside them and use all of it.",
          "Every deliverable moved through structured review with brand, marketing and regional stakeholders. I built the work so that feedback landed on substance rather than compliance: fewer rounds, cleaner decisions, and a standard that held across teams and regions.",
        ],
      },
      {
        num: "03",
        title: "Execution & Craft",
        body: [
          "The work was layout-led and typography-first: campaign and communications pieces built on the corporate grid and pushed as far as the system allows. Where the brand leaves room for expression, I took it. Where it doesn't, the discipline is the design.",
          "Everything was delivered to production standard for print and digital, with clean masters that regional teams could pick up, localise and ship without a designer in the room.",
        ],
      },
      {
        num: "04",
        title: "Trade-offs & Decisions",
        body: [
          "The standing decision on accounts like this is restraint over novelty. A clever layout that bends the system costs more in review cycles than it earns in attention, so I spent the energy on hierarchy, rhythm and detail, the things that make a compliant page feel considered rather than templated.",
        ],
      },
      {
        num: "05",
        title: "Outcome",
        body: [
          "The work shipped across teams and channels throughout 2023–24, and the engagement was extended across successive seasons. On an institution of this scale, being asked back is the metric that matters.",
        ],
      },
    ],
  },
  {
    slug: "orica",
    route: "corporate",
    title: "Presentation & Deck Design",
    client: "Orica",
    year: "Undisclosed",
    role: "Designer",
    sector: "Mining Services & Technology",
    tags: ["Presentation Design", "Information Design", "Data Visualisation"],
    summary:
      "Presentation and deck design for Orica. Dense talent, performance and succession data translated into clear, coherent, presentation-ready communication.",
    heroImage: "/images/orica/slide-02.jpg",
  },
  {
    slug: "weird-weather",
    route: "creative",
    title: "Weird Weather",
    client: "Weird Weather, fine fragrance",
    year: "2024",
    role: "Brand Identity, Packaging & Art Direction",
    sector: "Fragrance",
    tags: ["Identity", "Packaging", "Art Direction"],
    summary:
      "A high-end perfume house built from scratch: logo, brand identity and packaging, plus a customised brand bible to hold it all together.",
    heroImage: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1715849492587-5B0PLGTR4M1CZAS0C1TL/FINALPOST-21.jpg",
    story: [
      { type: "quote", text: "Strange weather, bottled. A brand built to be felt before it is read." },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1715849487378-LQ5GFXRDLMKOVMA1EJUV/done-27.jpg",
        caption: "Campaign key visual",
        wide: true,
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1715794700855-WO3HKCVHMFK2CU8JWD7Q/PORT610-07.jpg",
        caption: "Bottle & carton studies",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1715809494661-P20TTX9INNTZJTHZRCPI/stacked+box+mockup+weird+weatherpsd.jpg",
        caption: "Packaging system",
      },
      {
        type: "text",
        heading: "The brand bible",
        body: "Every rule of the house (mark, type, colour, tone, application) gathered into a customised brand bible, so the identity survives contact with every printer, developer and social manager it will ever meet.",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1715794154907-ENYNAR5M9ETN21R1UKLR/WEIRDWEATHER_Official_BrandGuide+150520246.jpg",
        caption: "Brand bible spreads",
        wide: true,
      },
      {
        type: "palette",
        colors: [
          { hex: "#0B0C0E", name: "Midnight" },
          { hex: "#D4A373", name: "Amber" },
          { hex: "#6B705C", name: "Vetiver" },
          { hex: "#F7F6F3", name: "Bone" },
        ],
      },
    ],
  },
  {
    slug: "unreasonable-land-nft",
    route: "creative",
    title: "Unreasonable Land NFT",
    client: "The Unreasonable Land",
    year: "2022",
    role: "Illustration, Character Design, UX & Roadmap",
    sector: "Web3 & Digital Art",
    tags: ["NFT", "Illustration", "Animation", "UX"],
    summary:
      "Character illustration, animated artwork, product roadmap and the full UX design map for The Unreasonable Land: an NFT collection whose initial purchase tokenised entry to a small online game.",
    focal: "center 40%",
    heroImage: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1647354579304-PW72VRKG66DNLN91V34V/jme+nu.gif",
    story: [
      { type: "quote", text: "Bing bong. The price is wrong." },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1647354579304-PW72VRKG66DNLN91V34V/jme+nu.gif",
        caption: "Animated NFT characters",
        aspect: "aspect-square",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1647354556522-0O84KTFHJBONMP7IMCOX/binggg.png",
        caption: "Tradespotting badge artwork",
        aspect: "aspect-square",
        contain: true,
        bg: "#F3F1EA",
      },
      {
        type: "text",
        heading: "More than a collection",
        body: "The Unreasonable Land was conceived as a small online game, not just a set of collectibles. The initial NFT purchase acted as the token of entry: holding a character unlocked the world, its trading mechanics and its progression. My role covered both the pictures and the plan.",
      },
      {
        type: "text",
        heading: "Roadmap and UX design map",
        body: "I created the project roadmap, sequencing the mint, the reveal, the game's first playable loop and its later phases so the community could see where the token would take them. Alongside it I designed the full UX map for the game: every screen, state and transition from wallet connect and character selection through trading, inventory and the Tradespotting market, so that the illustration and the product were designed as one system.",
      },
      {
        type: "text",
        heading: "Drawn like a bootleg, minted like an original",
        body: "One-colour badge lettering, a rubber-hose monkey and deliberately rough print texture on one side; a 63-frame pixel animation on the other. Two visual dialects for one collection, made for screens and for the culture that trades on them.",
      },
      {
        type: "palette",
        colors: [
          { hex: "#D0201C", name: "Badge red" },
          { hex: "#F3F1EA", name: "Newsprint" },
          { hex: "#12B03B", name: "Frog green" },
          { hex: "#F6DCF5", name: "Pastel pink" },
        ],
      },
    ],
  },
  {
    slug: "club-psychedelia",
    route: "creative",
    title: "Club Psychedelia",
    client: "Club Psychedelia, Paris",
    year: "2025",
    role: "Identity & Motion Graphics",
    sector: "Music & Nightlife",
    tags: ["Identity", "Motion", "Nightlife"],
    summary:
      "Identity and animated artwork for a Paris club night. I designed the graphics to move as hard as the music, then flattened them into posters and posts.",
    heroImage: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1737893088157-YNNK6MNQFCRKB3Y9L0BJ/harriet-psychadelia-clubnight-gif.gif",
    story: [
      { type: "quote", text: "A club-night identity should never stand still." },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1737893088157-YNNK6MNQFCRKB3Y9L0BJ/harriet-psychadelia-clubnight-gif.gif",
        caption: "Animated flyer system",
        wide: true,
      },
      {
        type: "text",
        heading: "Motion first",
        body: "The artwork was conceived as animation and only then flattened into posters and posts, so every static piece is a frame of something that moves.",
      },
    ],
  },
  {
    slug: "larsson-jennings",
    route: "creative",
    title: "Larsson & Jennings",
    client: "Larsson & Jennings",
    year: "2018",
    role: "Design & Animation",
    sector: "Watches & Fashion",
    tags: ["Animation", "Campaign"],
    summary:
      "Animated campaign artwork for Larsson & Jennings: the Swedish-British watchmaker's minimalism, set in motion.",
    heroImage: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1542975736486-IS6HWX1WT5BX2HB4YIK8/larrson+and+jennings+animation.gif",
    story: [
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1542975736486-IS6HWX1WT5BX2HB4YIK8/larrson+and+jennings+animation.gif",
        caption: "Campaign animation",
        wide: true,
      },
      {
        type: "text",
        heading: "Stillness, animated",
        body: "A study in restraint: campaign motion for a brand built on quiet, precise objects. The animation had to tick, not shout.",
      },
    ],
  },
  {
    slug: "sunset-social-club",
    route: "creative",
    title: "Sunset Social Club",
    client: "Sunset Social Club",
    year: "2024",
    role: "Identity & Campaign Design",
    sector: "Events & Culture",
    tags: ["Identity", "Campaign", "Social"],
    summary:
      "Identity and campaign artwork for Sunset Social Club, a gathering built around golden hour.",
    heroImage: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/8dc3f826-791e-4161-b5bc-cee4041be8de/SSCJuneDOZEN+post+2.jpg",
    story: [
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/8dc3f826-791e-4161-b5bc-cee4041be8de/SSCJuneDOZEN+post+2.jpg",
        caption: "Campaign post, June series",
        wide: true,
      },
      {
        type: "text",
        heading: "Golden hour, systemised",
        body: "A flexible identity and social campaign system: artwork that can carry a date, a lineup or a mood, and still read instantly as the Club.",
      },
    ],
  },
  {
    slug: "heartbreakers",
    route: "creative",
    title: "Heartbreakers",
    client: "Heartbreakers, bar & venue",
    year: "2019",
    role: "Brand Identity",
    sector: "Hospitality",
    tags: ["Identity", "Venue"],
    summary:
      "Brand identity for Heartbreakers, a bar and venue with a name that does half the talking. I made sure the design did the rest: bold, noirish and built for low light.",
    heroImage: "/images/heartbreakers-neon.jpg",
    story: [
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1554147261769-5Y9E9FHASENQC7PETWBU/Screen+Shot+2019-04-01+at+21.32.57.png",
        caption: "Identity in situ",
        wide: true,
      },
      {
        type: "text",
        heading: "A name with a pulse",
        body: "The identity leans into the melodrama of the name: bold, noirish and built for low light.",
      },
    ],
  },
  {
    slug: "abaeran",
    route: "creative",
    title: "Abaeran",
    client: "Abaeran",
    year: "2018",
    role: "Brand Identity & Guidelines",
    sector: "Retail",
    tags: ["Identity", "Guidelines"],
    summary: "Rebranding for Abaeran, a Japanese retailer: logo design and a brand guidelines document to carry the new identity consistently.",
    focal: "center 45%",
    heroImage: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1532432658050-4Z4Q3U2A7YKC3YA7NSSN/abcov.jpg",
    story: [
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1532432658050-4Z4Q3U2A7YKC3YA7NSSN/abcov.jpg",
        caption: "Brand identity",
        wide: true,
      },
      {
        type: "text",
        heading: "Logo and guidelines",
        body: "The rebrand covered the design of a new logo and the creation of a brand guidelines document, setting out how the identity is applied across the retailer's communications.",
      },
    ],
  },
  {
    slug: "dazy-drinks",
    route: "creative",
    title: "Dazy Drinks",
    client: "Dazy Drinks",
    year: "2025",
    role: "Brand Identity & Packaging",
    sector: "Beverages",
    tags: ["Packaging", "Identity"],
    summary: "Brand identity and packaging for Dazy Drinks: a name, mark and can built to be found fast on a crowded fridge shelf and remembered after the first sip.",
    focal: "center 48%",
    heroImage: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1737891752839-G07KFY8SY56T5QUGYYVN/Screenshot+2025-01-26+at+11.40.37.png",
    story: [
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1737891752839-G07KFY8SY56T5QUGYYVN/Screenshot+2025-01-26+at+11.40.37.png",
        caption: "Identity & packaging",
        wide: true,
      },
      {
        type: "text",
        heading: "Built for the fridge door",
        body: "Drinks branding lives or dies at arm's length. Colour, mark and name tuned to be found fast and remembered faster.",
      },
    ],
  },
  {
    slug: "hanging-gardens",
    route: "creative",
    title: "Hanging Gardens",
    client: "Hanging Gardens",
    year: "2024",
    role: "Brand Identity & Signage",
    sector: "Hospitality",
    tags: ["Identity", "Signage"],
    summary:
      "Brand identity for Hanging Gardens: a soft serif wordmark, a considered set of applications and signage designed to be read from the street.",
    heroImage: "/images/hanging-gardens-logo.jpg",
    story: [
      { type: "quote", text: "A name that already sounds like a place. The identity just had to make it feel like one." },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1715596800358-R6DHI70PO6VL1J2HH5K0/hanginggardens+logo-04.jpg",
        caption: "Wordmark",
        wide: true,
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1715598006911-KUT0F45RSGQ8WOY4NYRD/Screenshot+2024-05-13+at+11.59.46.png",
        caption: "Identity overview",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1715598517757-L8EKM2U151L8TJCCPRSN/Screenshot+2024-05-13+at+12.04.34.png",
        caption: "Applications",
      },
      {
        type: "text",
        heading: "Lush, but disciplined",
        body: "The mark carries the softness; everything around it stays quiet. A restrained palette and a strict layout system keep the identity legible across signage, print and screen.",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1715598507516-2DPV6TA4NCI62QXX12CC/Screenshot+2024-05-13+at+12.03.22.png",
        caption: "Brand applications, continued",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1715598530734-96S0HAQ6YMVNF1P9R6Q4/Screenshot+2024-05-13+at+12.06.30.png",
        caption: "Identity in use",
        wide: true,
      },
    ],
  },
  {
    slug: "sivu-sound-of-hope",
    route: "creative",
    title: "SIVU: Sound of Hope",
    client: "SIVU, Cannes Lions",
    year: "2022",
    role: "Campaign & Exhibition Design",
    sector: "Health Technology",
    tags: ["Campaign", "Exhibition"],
    summary:
      "Campaign board and poster design for SIVU's Sound of Hope case study, presented at Cannes: a hearing-aid story told through documentary photography and bold editorial type.",
    heroImage: "/images/sivu-banner-crop.jpg",
    story: [
      { type: "quote", text: "Problem, solution, results. A case study layout that lets the human story lead." },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1667471734763-5260JSVUS1GGB6OA7DPX/Hearing-Aid-Poster_2022-04-27_1204+%E2%80%93+Provisional+Draft+%28option+B%29.jpg",
        caption: "Campaign poster",
        wide: true,
      },
      {
        type: "text",
        heading: "Editorial at exhibition scale",
        body: "A three-column narrative structure, condensed display type and full-bleed photography, designed to be read in seconds by a passing jury.",
      },
    ],
  },
  {
    slug: "vortex-events",
    route: "creative",
    title: "Vortex Events",
    client: "Vortex Events",
    year: "2024",
    role: "Poster Design & Illustration",
    sector: "Events & Nightlife",
    tags: ["Illustration", "Poster"],
    summary:
      "Poster artwork for Vortex Events: crumpled-paper textures, a hand-drawn character and typography that bends the way the night does.",
    heroImage: "/images/vortex-character.jpg",
    story: [
      { type: "quote", text: "Enter the Vortex. But be warned, you will not be the same when you return." },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1715314593883-80YRWDWXQ4TA87LEW6ZJ/Screenshot+2024-05-10+at+05.15.05.png",
        caption: "Poster series",
        wide: true,
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1715314594919-BM87Z1ELJ0B47LBTP4Z6/Screenshot+2024-05-10+at+05.15.20.png",
        caption: "Detail",
      },
      {
        type: "text",
        heading: "Paper first",
        body: "The artwork was built to look handled: torn edges, creases and ink that sits on the surface. Digital posters that behave like they were pulled off a wall.",
      },
    ],
  },
  {
    slug: "pollenize",
    route: "creative",
    title: "Pollenize CIC",
    client: "Pollenize CIC",
    year: "2021",
    role: "Brand Identity, Guidelines & Print",
    sector: "Environmental Non-profit",
    tags: ["Identity", "Print", "Guidelines"],
    summary:
      "Brand identity, guidelines and print collateral for Pollenize, a Cornish community-interest company rewilding for pollinators: a bright, honeyed mark with a full brand book behind it.",
    focal: "center 32%",
    heroImage: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1716386623262-OFYXU0XZIARUQU13AMKT/Pollenize%2BLogo%2BB%2B%E2%80%93%2BWeb-06.jpeg",
    story: [
      { type: "quote", text: "A brand for bees, built for the people who look after them." },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1716386735352-B0ZKI58B08Q182X64TDT/Pollenize_deck.jpeg",
        caption: "Brand guidelines",
        wide: true,
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1716386735217-37G91T3IA3JOCV0D4Z7E/Pollenize%2Bbook%2Bmockup%2B5.jpeg",
        caption: "Brand book spreads",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1716386736285-QOAGYFAZP7BTWX11VOG5/Pollenize%2Bbook%2Bmockup%2B6.jpeg",
        caption: "Brand book spreads",
      },
      {
        type: "text",
        heading: "From mark to membership",
        body: "Beyond the identity: patronage leaflets, beekeeper information sheets and merchandise, each carrying the same yellow and the same plain-spoken tone.",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1620765459241-1S53UWXQRAPBO9YDYEZS/Pollenize+Patronage+Leaflet+Apr21+Front-01.jpg",
        caption: "Patronage leaflet",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1620765501921-TZIQ18LTF13IG7PHBVJK/Cornwall+Beekeepers+info+sheet+final+layout-02.jpg",
        caption: "Cornwall Beekeepers information sheet",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1620765411228-20OO2B6YC0HKTBIJ8K66/CF+2mb+POLLENIZEPICS-03.jpg",
        caption: "Identity in the field",
        wide: true,
      },
      {
        type: "palette",
        colors: [
          { hex: "#F2B705", name: "Honey" },
          { hex: "#1C1C1C", name: "Soil" },
          { hex: "#F7F6F3", name: "Wax" },
          { hex: "#6B8E23", name: "Meadow" },
        ],
      },
    ],
  },
  {
    slug: "cosmic-kitchen",
    route: "creative",
    title: "Cosmic Kitchen",
    client: "Cosmic Kitchen, Plymouth",
    year: "2023",
    role: "Poster Design & Illustration",
    sector: "Hospitality",
    tags: ["Illustration", "Poster", "Identity"],
    summary:
      "Poster and identity artwork for Cosmic Kitchen, a vegan café and deli in Plymouth: a fork orbiting a planet, psychedelic lettering and a menu written into the rings.",
    heroImage: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1693980917841-E73I0XRGUQG3IJUGA2F0/cosmic+poster.png",
    story: [
      { type: "quote", text: "Vegan everything, drawn like a gig poster." },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1693980917841-E73I0XRGUQG3IJUGA2F0/cosmic+poster.png",
        caption: "Poster artwork",
        aspect: "aspect-[3/4]",
        contain: true,
        bg: "#0B0C0E",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1693980953406-QJIH2GTWBOQ35GV6APBR/COSMICSESSIONS-48.jpg",
        caption: "Cosmic Sessions artwork",
        aspect: "aspect-[3/4]",
      },
      {
        type: "text",
        heading: "The menu as orbit",
        body: "Every dish sits in the planet's rings, so the poster does the job of a menu board while still reading as a piece of art on the wall.",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/0afeeaa4-7891-495c-9d0e-3d3714fae987/Screenshot+2025-01-26+at+11.35.51.png",
        caption: "Identity in use",
        wide: true,
      },
    ],
  },
  {
    slug: "chase-the-night",
    route: "creative",
    title: "Chase the Night",
    client: "Paul Honey",
    year: "2023",
    role: "Poster Design & Illustration",
    sector: "Music & Events",
    tags: ["Illustration", "Poster"],
    summary:
      "Poster and social artwork for Chase the Night: a hand-drawn runner on strings, a hand reaching from a hole in the page and type that leans forward like it is late.",
    heroImage: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1699301247996-3ENZIXUZ1KIWF8B7MO8Y/CHASETHENIGHT+POSTERHJ.jpg",
    story: [
      { type: "quote", text: "A puppet running from the hand that holds it." },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1699301247996-3ENZIXUZ1KIWF8B7MO8Y/CHASETHENIGHT+POSTERHJ.jpg",
        caption: "Poster artwork",
        aspect: "aspect-[3/4]",
        contain: true,
        bg: "#E9E5DC",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1699301229053-DGXPWP6XKFEYNBHCVN7Z/AAAAchasethenight-40.jpg",
        caption: "Artwork detail",
        aspect: "aspect-[3/4]",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1699301231100-5GM3TNXVJOQMGXHEMB6E/AAAAchasethenight-42.jpg",
        caption: "Social variant",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1699301244679-4E0OPQ83COZ3FDHAPWT3/aaachasethenight+copy.jpg",
        caption: "Type study",
      },
    ],
  },
  {
    slug: "dont-walk-boogie",
    route: "creative",
    title: "Don't Walk, Boogie!",
    client: "Don't Walk, Boogie!",
    year: "2018 to 2019",
    role: "Illustration & Event Artwork",
    sector: "Music & Events",
    tags: ["Illustration", "Poster", "Campaign"],
    summary:
      "A run of illustrated event artwork for Don't Walk, Boogie!, a London disco night: crowds of dancing characters, liquid shapes and hand-lettered line-ups for shows at Phonox and beyond.",
    heroImage: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1560951715662-1V6LPLFXDSD3ZGQD1AC6/DWB+PHONOX+NEW-07.jpg",
    story: [
      { type: "quote", text: "One night, many posters. The crowd changes; the joy does not." },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1560951715662-1V6LPLFXDSD3ZGQD1AC6/DWB+PHONOX+NEW-07.jpg",
        caption: "Phonox, London",
        wide: true,
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1545551410618-CYS5W6XWQPMUMA573R73/fingerman+final-09.jpg",
        caption: "Fingerman",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1550811910371-AY9KINZQ38QENK4HPHF8/DWB+JIMMYNEW5-10.jpg",
        caption: "Jimmy",
      },
      {
        type: "text",
        heading: "A cast, not a template",
        body: "Each show got its own illustrated crowd and its own lettering, held together by a recurring palette and a hand that never went digital-clean. The series built a recognisable world one flyer at a time.",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1548655147534-WXOUTTM53RJAWMPFG207/DWB+Laroze-18.jpg",
        caption: "Laroze",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1553103491783-8LEVE3ZI8LOTGNCD3SOS/DWB+FUTURE-02.jpg",
        caption: "Future",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1560951658459-AN6UB3OOO4SIXO0ZZPDV/DWB+grad+week+special-02.jpg",
        caption: "Grad week special",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1560951723239-BRUWCKBL9BFB8MTAPMRH/dwbmock.jpg",
        caption: "Poster in situ",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1699432965807-MS1TI8G0JV1UAOI9YKEA/Screenshot+2023-11-08+at+08.41.53.png",
        caption: "Series overview",
        wide: true,
      },
    ],
  },
  {
    slug: "lightworks",
    route: "creative",
    title: "L*ghtw'rks",
    client: "L*ghtw'rks, The Bread & Roses",
    year: "2018",
    role: "Identity & Poster Design",
    sector: "Arts & Events",
    tags: ["Identity", "Poster", "Illustration"],
    summary:
      "Identity and poster series for L*ghtw'rks, an arts night at The Bread & Roses: a wordmark with its vowels swapped for a lamp, and a run of posters built on light, shadow and one hard orange.",
    heroImage: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1532437491607-RRNA1IEO7WB265QAXV54/Mockup+lightworks+final+jun18.jpg",
    story: [
      { type: "quote", text: "Take the vowels out. Put a light in." },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1532437491607-RRNA1IEO7WB265QAXV54/Mockup+lightworks+final+jun18.jpg",
        caption: "Poster, June 2018",
        wide: true,
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1532362738643-9ZLRGKJL209ZVI4VVECL/l1.jpg",
        caption: "Series 01",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1532362739528-5MBPWNPYD54X2WAIY6EV/l2.jpg",
        caption: "Series 02",
      },
      {
        type: "text",
        heading: "A system with one rule",
        body: "Black, paper and a single orange. Every poster in the series plays the same three notes, so the night is recognisable from across the pub before the name is legible.",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1532362741412-K8I5ZGYERO54H33SWHM6/l4.jpg",
        caption: "Series 04",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1532362742625-SMNAIOUAZYAX8D3786PQ/l6.jpg",
        caption: "Series 06",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1532362745505-E8A6X9NED3YE8AFI1UNE/l9.jpg",
        caption: "Series 09",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1532362753115-MVYNNR6KDHIBOX4NI98K/l14.jpg",
        caption: "Series 14",
      },
      {
        type: "palette",
        colors: [
          { hex: "#F26B1D", name: "Lamp" },
          { hex: "#111111", name: "Black" },
          { hex: "#F4F1EA", name: "Paper" },
        ],
      },
    ],
  },
  {
    slug: "tusk-sports-bar",
    route: "creative",
    title: "Tusk Sports Bar",
    client: "Tusk Sports Bar",
    year: "2024",
    role: "Brand Identity",
    sector: "Hospitality",
    tags: ["Identity", "Typography"],
    summary:
      "Brand identity for Tusk Sports Bar: a custom geometric alphabet, a green and gold palette and a system that runs from menus to match-day screens.",
    heroImage: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1715186082305-OX3NAYCDCJ9N7RD2HXOS/tusk2.jpeg",
    story: [
      { type: "quote", text: "A sports bar with a typeface of its own." },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1715186082305-OX3NAYCDCJ9N7RD2HXOS/tusk2.jpeg",
        caption: "Custom alphabet",
        wide: true,
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1715186082582-94YCVOXJI4048DSJ85NZ/tusk5.jpeg",
        caption: "Identity",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1715186083779-LFEA7BGANCWSXAJI7JMU/tusk6.jpeg",
        caption: "Applications",
      },
      {
        type: "text",
        heading: "Built to be shouted",
        body: "Wide, upright letterforms that hold up on a screen behind the bar and on a coaster under a pint. Green for the pitch, gold for the trophy.",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1715186084958-18DBL3JTRWUXZGK1VV3U/tusk8.jpeg",
        caption: "Applications",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1715186086095-8WM3HNV1V46GZIOBR2MI/tusk10.jpeg",
        caption: "Applications",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1715186087184-ZIRX7HIO0O1K2VE1HXNW/tusk12.jpeg",
        caption: "Applications",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1715186092604-L8NYKZR1YR1ILCCIN0KT/tusk20.jpeg",
        caption: "Identity in use",
      },
      {
        type: "palette",
        colors: [
          { hex: "#0F5C4A", name: "Pitch" },
          { hex: "#E3B23C", name: "Trophy" },
          { hex: "#F7F5EE", name: "Chalk" },
        ],
      },
    ],
  },
  {
    slug: "alexander-and-ellis",
    route: "creative",
    title: "Alexander & Ellis",
    client: "Alexander & Ellis",
    year: "2021",
    role: "Brand Identity & Illustration",
    sector: "Craft & Retail",
    tags: ["Identity", "Illustration", "Print"],
    summary:
      "Brand identity for Alexander & Ellis: a single-line etching of a maker at work, gold on deep green, with a small pinecone-and-wing crest for the details.",
    heroImage: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1621957760423-UANGE77NFBDD9D5M9BC6/Alexander+EllisFront-14.jpg",
    story: [
      { type: "quote", text: "An heirloom brand for things made by hand." },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1621957760423-UANGE77NFBDD9D5M9BC6/Alexander+EllisFront-14.jpg",
        caption: "Identity, front",
        aspect: "aspect-[3/4]",
        contain: true,
        bg: "#0F3F36",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1621957891137-PELEW52VZX65RDRK6CTX/IMG_1214.jpg",
        caption: "Print application",
        aspect: "aspect-[3/4]",
      },
      {
        type: "text",
        heading: "Engraved, not drawn",
        body: "The illustration was built line by line to sit like an engraving on stock, so that in gold foil or a single ink it still reads as a plate rather than a picture.",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1621957891151-L1HK3MWJQ8OGK866RFUX/IMG_1215.jpg",
        caption: "Print application",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1621957894858-5GPV3DWD3V4NF4ZNF17I/IMG_1216.jpg",
        caption: "Print application",
      },
      {
        type: "palette",
        colors: [
          { hex: "#0F3F36", name: "Forest" },
          { hex: "#C9A24A", name: "Gold" },
          { hex: "#F4F1E8", name: "Stock" },
        ],
      },
    ],
  },
  {
    slug: "parisian-sweet",
    route: "creative",
    title: "Parisian Sweet",
    client: "Parisian Sweet",
    year: "2018",
    role: "Identity, Illustration & Campaign",
    sector: "Fashion & Beauty",
    tags: ["Identity", "Illustration", "Campaign"],
    summary:
      "Identity and campaign artwork for Parisian Sweet: a hand-drawn pair of eyes peering through fingers, a high-contrast wordmark and a line that promises you won't believe them.",
    heroImage: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1541927677678-RK2Q610ZENJJR41GJ00Q/parisiansweeeet.jpg",
    story: [
      { type: "quote", text: "You won't believe your eyes." },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1541928221568-CZWBWCAR0EG8VQNIJC12/parisianbus.jpg",
        caption: "Campaign, bus shelter",
        wide: true,
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1541927677678-RK2Q610ZENJJR41GJ00Q/parisiansweeeet.jpg",
        caption: "Identity",
        aspect: "aspect-[3/4]",
        contain: true,
        bg: "#F7F5F2",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1541927095341-19VOIJUNVEFX69UO6J57/parisian.png",
        caption: "Wordmark",
        aspect: "aspect-[3/4]",
        contain: true,
        bg: "#F7F5F2",
      },
      {
        type: "text",
        heading: "Ink and attitude",
        body: "One illustration, one wordmark, one colour. The restraint is what lets it hold a bus shelter at ten metres and a lipstick box at ten centimetres.",
      },
    ],
  },
  {
    slug: "tree-doctor",
    route: "creative",
    title: "Tree Doctor",
    client: "Tree Doctor",
    year: "2021",
    role: "Brand Identity",
    sector: "Arboriculture",
    tags: ["Identity", "Print"],
    summary:
      "Brand identity for Tree Doctor, an arborist practice: a cross built from branches, gold on matte black, and stationery that feels closer to a surgery than a garden centre.",
    heroImage: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1620763081079-U8IS3CNXFSM58TX9CGRZ/treedocbranding+mock.jpg",
    story: [
      { type: "quote", text: "First, do no harm. Then prune." },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1620763081079-U8IS3CNXFSM58TX9CGRZ/treedocbranding+mock.jpg",
        caption: "Stationery",
        wide: true,
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1620763204584-M1Y97CG4L3NOJVCH4B41/TREEDOC+JPEGnew-04.jpg",
        caption: "Identity",
        wide: true,
      },
      {
        type: "text",
        heading: "Medical, but warm",
        body: "The cross borrows the authority of medicine; the branches give it back to the garden. Black and gold keep it out of the muddy-green territory the trade defaults to.",
      },
    ],
  },
  {
    slug: "sister-fix",
    route: "creative",
    title: "Sister Fix / Momentum",
    client: "Momentum",
    year: "2018",
    role: "Poster Design & Illustration",
    sector: "Fundraising & Events",
    tags: ["Illustration", "Poster", "Campaign"],
    summary:
      "Poster series for Sister Fix, a fundraiser for Sisters Uncut hosted by Momentum: a chain of linked hands, red type and a line-up set in the gaps.",
    heroImage: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1532448326041-EUPQH20RUYBZK19ALE68/SISTER+FIX+HJONES+POSTER1.jpg",
    story: [
      { type: "quote", text: "Hands holding hands. The poster is the point." },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1532448326041-EUPQH20RUYBZK19ALE68/SISTER+FIX+HJONES+POSTER1.jpg",
        caption: "Sister Fix poster",
        aspect: "aspect-[3/4]",
        contain: true,
        bg: "#15161C",
      },
      {
        type: "text",
        heading: "Built from one gesture",
        body: "A single drawn hand, repeated and interlocked, became the whole visual language. It carried the first event, the Christmas edition and every social post between.",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1544375323085-6NKZW8N46UQMU4MKH2J9/SisterFixmasCov-03.jpg",
        caption: "Event cover artwork",
        wide: true,
      },
    ],
  },
  {
    slug: "another-world-farms",
    route: "creative",
    title: "Another World Farms",
    client: "AWF",
    year: "2020",
    role: "Brand Identity & Guidelines",
    sector: "Agriculture & Technology",
    tags: ["Identity", "Guidelines"],
    summary:
      "Brand identity and guidelines for Another World Farms: a stacked-layer mark with a rising sun, a monochrome system and a brand book that sets out how it grows.",
    focal: "center 42%",
    heroImage: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1716385718570-ZRVTJ0X84DI00XS3OOBU/AWF%2Blogo%2B13.03.20-02.jpg",
    story: [
      { type: "quote", text: "Layers of growing, one sun above." },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1716385718570-ZRVTJ0X84DI00XS3OOBU/AWF%2Blogo%2B13.03.20-02.jpg",
        caption: "Primary mark",
        aspect: "aspect-square",
        contain: true,
        bg: "#F5F3EE",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1716385718499-XMSEBMH3VPSP48SB4SPW/AWF%2Blogo%2B13.03.20-03.jpg",
        caption: "Mark, reversed",
        aspect: "aspect-square",
        contain: true,
        bg: "#111111",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1716385736368-IGKPXUIYIJ2PTUTFSGRA/AWF%2Bbook%2Bmockup%2Bpg1.jpeg",
        caption: "Brand guidelines",
        wide: true,
      },
      {
        type: "text",
        heading: "A mark that stacks",
        body: "The layered trays of a vertical farm, drawn as three planes with the sun sitting on top. It works as a favicon, a stencil and a full-page title, and the guidelines make sure it stays that simple.",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1716385727872-LNXAR44610CMUYVDOOXF/AWF%2Bsocial%2Bmedia%2Bfacebook%2Bcover-13.jpeg",
        caption: "Social cover",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1716385736871-80ZW7IBL38HG5ZRGE475/AWF%2Bcolour%2Bscheme%2B13.03.20-06.jpg",
        caption: "Colour scheme",
      },
    ],
  },
  {
    slug: "run-for-child-poverty",
    route: "creative",
    title: "Run! For Child Poverty",
    client: "Run! For Child Poverty",
    year: "2018",
    role: "Brand Identity & Print",
    sector: "Charity",
    tags: ["Identity", "Print", "Campaign"],
    summary:
      "Brand identity and print for Run! For Child Poverty, a charity born in 2018 to build homes, fund education and use sport to help children out of poverty: a running figure, a bold yellow and a pattern that never stops moving.",
    heroImage: "/images/rfcp-logo.jpg",
    story: [
      { type: "quote", text: "Let's run." },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1541928722270-3WIEQ4Q9KGQFRJG88JWO/RFCP+MOCK.jpg",
        caption: "Leaflet and pattern",
        wide: true,
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1541928694437-SYK0DCCNC57C34ND1YRZ/Screen+Shot+2018-11-11+at+09.29.40.png",
        caption: "Identity",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1541928694698-PCXZ5QLS7GXYG3PW9TG6/Screen+Shot+2018-11-11+at+09.29.49.png",
        caption: "Identity",
      },
      {
        type: "text",
        heading: "Built to be photocopied",
        body: "A charity identity has to survive a church-hall printer. One colour, one runner and a repeat pattern made from the letter R keep it recognisable at any quality.",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1541928695553-0XZD0SVSA5AFC88FWAIW/Screen+Shot+2018-11-11+at+09.29.57.png",
        caption: "Applications",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1541928695899-W12WEARF1OH7L1PQ3FS5/Screen+Shot+2018-11-11+at+09.30.10.png",
        caption: "Applications",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1541928697388-7TDRFYY0KUVSF0E5VU2B/Screen+Shot+2018-11-11+at+09.30.20.png",
        caption: "Applications",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1541928700893-4AWP2RMGIAAHF1AMKE9X/Screen+Shot+2018-11-11+at+09.30.46.png",
        caption: "Applications",
      },
      {
        type: "palette",
        colors: [
          { hex: "#F5D800", name: "Run yellow" },
          { hex: "#111111", name: "Black" },
          { hex: "#FFFFFF", name: "White" },
        ],
      },
    ],
  },
  {
    slug: "renzo-leonard-street",
    route: "creative",
    title: "Renzo, Leonard Street",
    client: "Renzo",
    year: "2021 to 2022",
    role: "Identity, Illustration & Merch",
    sector: "Hospitality",
    tags: ["Identity", "Illustration", "Print"],
    summary:
      "Identity, illustration and merchandise for Renzo, a coffee and food spot on Leonard Street: a hand-drawn regular with an espresso, an arched wordmark and a lockdown-era tee that says what everyone was thinking.",
    heroImage: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1621959672711-2VGAWD3JNSOYKKM5PB0H/RENZNU-05.jpg",
    story: [
      { type: "quote", text: "Coffee and food since summer '16." },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1621959665554-T4XERAL9TNZT4S9H51QM/RENZNU-01.jpg",
        caption: "Primary lock-up",
        aspect: "aspect-square",
        contain: true,
        bg: "#EDEFE3",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1666772366321-GRZ9G8PZ5JAB11AX3ZF9/Screenshot+2022-10-26+at+10.19.00.png",
        caption: "Don't fraternise, just sanitise",
        aspect: "aspect-square",
        contain: true,
        bg: "#F3EFE2",
      },
      {
        type: "text",
        heading: "A neighbourhood character",
        body: "The illustration is the regular at the window seat: hoodie, espresso, unbothered. It carries the identity onto cups, tees and the shopfront without needing the name beside it.",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1621959665431-JJX03CEEIUW2W6LVYV1O/RENZNU-02.jpg",
        caption: "Variant",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1621959671202-T8CNZMDOU2QP3S7O3PSF/RENZNU-03.jpg",
        caption: "Variant",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1621959672711-2VGAWD3JNSOYKKM5PB0H/RENZNU-05.jpg",
        caption: "Variant",
      },
      {
        type: "image",
        src: "https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1621959687388-X11LGBTI9WOJJGI8LETT/RENZO+Back-15.jpg",
        caption: "Tee, back print",
      },
    ],
  },
];

export const corporateStats = [
  { value: "59+", label: "Commissions to date" },
  { value: "16", label: "Global institutions" },
  { value: "B2B+B2C", label: "Channels delivered" },
  { value: "Remote", label: "Worldwide practice" },
];

export const clients = [
  "JP Morgan & Chase",
  "Cathay Pacific",
  "Larsson & Jennings",
  "Pollenize CIC",
  "SIVU",
  "The Bread & Roses",
  "Heartbreakers",
];
